/**
 * Template Requirements Checker
 * Validates runtime requirements and Google Ads API v22 compliance for templates
 */

import { IndustryTemplate, TemplateValidationResult } from '../types/industryTemplate';
import { industryTemplateService } from './industryTemplateService';
import { db } from '../config/firebase';
import { doc, getDoc, collection, query, where, getDocs, Timestamp } from 'firebase/firestore';

export interface RuntimeRequirementsResult {
  canProceed: boolean;
  missing: string[];
  warnings: string[];
  instructions: Record<string, string>;
}

export const templateRequirementsChecker = {
  /**
   * Check all runtime requirements for a template
   * This checks actual org setup, not just template config
   */
  async checkTemplateRequirements(
    template: IndustryTemplate,
    orgId: string
  ): Promise<RuntimeRequirementsResult> {
    const missing: string[] = [];
    const warnings: string[] = [];
    const instructions: Record<string, string> = {};

    // 1. Check Merchant Center requirement
    if (template.requirements.merchantCenter) {
      const hasMerchantCenter = await this.checkMerchantCenter(orgId);
      if (!hasMerchantCenter) {
        missing.push('Merchant Center');
        instructions['Merchant Center'] = 
          'Connect your Google Merchant Center account in Settings → Merchant Center. ' +
          'This is required for e-commerce campaigns with product feeds.';
      }
    }

    // 2. Check Call Tracking requirement
    if (template.requirements.callTracking) {
      const hasCallTracking = await this.checkCallTracking(orgId);
      if (!hasCallTracking) {
        warnings.push('Call Tracking recommended');
        instructions['Call Tracking'] = 
          'Set up call tracking to measure phone conversions. ' +
          'Go to Settings → Conversions → Add Call Tracking.';
      }
    }

    // 3. Check Conversion Tracking (always required)
    if (template.requirements.conversionTracking) {
      const hasConversionTracking = await this.checkConversionTracking(orgId);
      if (!hasConversionTracking) {
        missing.push('Conversion Tracking');
        instructions['Conversion Tracking'] = 
          'Install Google Ads conversion tracking on your website. ' +
          'Go to Settings → Conversions → Set Up Tracking.';
      }
    }

    // 4. Check minimum conversions for ROAS bidding
    if (
      template.requirements.minimumConversions &&
      (template.campaignConfig.searchBiddingStrategy === 'target_roas' ||
       template.campaignConfig.pmaxBiddingStrategy === 'target_roas')
    ) {
      const conversionCount = await this.checkConversionCount(orgId, 30);
      const required = template.requirements.minimumConversions;
      
      if (conversionCount < required) {
        warnings.push(
          `Insufficient conversion data: ${conversionCount}/${required} conversions in last 30 days`
        );
        instructions['Conversion Data'] = 
          `TARGET_ROAS bidding performs best with at least ${required} conversions in 30 days. ` +
          'Consider using TARGET_CPA or MAXIMIZE_CONVERSIONS until you have enough data.';
      }
    }

    // 5. Check Google Ads connection
    const hasConnection = await this.checkGoogleAdsConnection(orgId);
    if (!hasConnection) {
      missing.push('Google Ads Connection');
      instructions['Google Ads Connection'] = 
        'Connect your Google Ads account in Settings → Google Ads Connection.';
    }

    return {
      canProceed: missing.length === 0,
      missing,
      warnings,
      instructions
    };
  },

  /**
   * Validate template against Google Ads API v22
   * This calls the existing validation from industryTemplateService
   */
  validateApiV22Compliance(template: IndustryTemplate): TemplateValidationResult {
    return industryTemplateService.validateApiV22Compliance(template);
  },

  /**
   * Check if Merchant Center is connected
   */
  async checkMerchantCenter(orgId: string): Promise<boolean> {
    try {
      const merchantCenterRef = doc(db, 'merchantCenterConnections', orgId);
      const merchantCenterDoc = await getDoc(merchantCenterRef);
      
      if (!merchantCenterDoc.exists()) {
        return false;
      }

      const data = merchantCenterDoc.data();
      return data.status === 'connected' && data.merchantId;
    } catch (error) {
      console.error('Error checking Merchant Center:', error);
      return false;
    }
  },

  /**
   * Check if Call Tracking is set up
   */
  async checkCallTracking(orgId: string): Promise<boolean> {
    try {
      const conversionsRef = collection(db, 'conversions');
      const q = query(
        conversionsRef,
        where('orgId', '==', orgId),
        where('type', '==', 'phone_call')
      );
      
      const snapshot = await getDocs(q);
      return !snapshot.empty;
    } catch (error) {
      console.error('Error checking call tracking:', error);
      return false;
    }
  },

  /**
   * Check if Conversion Tracking is installed
   */
  async checkConversionTracking(orgId: string): Promise<boolean> {
    try {
      const conversionsRef = collection(db, 'conversions');
      const q = query(conversionsRef, where('orgId', '==', orgId));
      
      const snapshot = await getDocs(q);
      return !snapshot.empty;
    } catch (error) {
      console.error('Error checking conversion tracking:', error);
      return false;
    }
  },

  /**
   * Count conversions in last N days
   */
  async checkConversionCount(orgId: string, days: number): Promise<number> {
    try {
      const conversionsRef = collection(db, 'campaignConversions');
      const cutoffDate = Timestamp.fromDate(
        new Date(Date.now() - days * 24 * 60 * 60 * 1000)
      );
      
      const q = query(
        conversionsRef,
        where('orgId', '==', orgId),
        where('timestamp', '>=', cutoffDate)
      );
      
      const snapshot = await getDocs(q);
      return snapshot.size;
    } catch (error) {
      console.error('Error counting conversions:', error);
      return 0;
    }
  },

  /**
   * Check if Google Ads is connected
   */
  async checkGoogleAdsConnection(orgId: string): Promise<boolean> {
    try {
      const connectionRef = doc(db, 'googleAdsConnections', orgId);
      const connectionDoc = await getDoc(connectionRef);
      
      if (!connectionDoc.exists()) {
        return false;
      }

      const data = connectionDoc.data();
      return data.status === 'connected' || data.status === 'demo';
    } catch (error) {
      console.error('Error checking Google Ads connection:', error);
      return false;
    }
  },

  /**
   * Get setup recommendations based on template
   */
  getSetupRecommendations(template: IndustryTemplate): string[] {
    const recommendations: string[] = [];

    if (template.requirements.merchantCenter) {
      recommendations.push(
        '🛒 Connect Google Merchant Center for product feed campaigns'
      );
    }

    if (template.requirements.callTracking) {
      recommendations.push(
        '📞 Set up call tracking to measure phone conversions'
      );
    }

    if (template.requirements.minimumConversions) {
      recommendations.push(
        `📊 Aim for at least ${template.requirements.minimumConversions} conversions in 30 days before using ROAS bidding`
      );
    }

    if (template.campaignConfig.searchBiddingStrategy === 'target_roas' ||
        template.campaignConfig.pmaxBiddingStrategy === 'target_roas') {
      recommendations.push(
        '💰 Ensure accurate conversion values are set for ROAS optimization'
      );
    }

    // Budget recommendations
    if (template.minimumDailyBudget) {
      recommendations.push(
        `💳 Minimum daily budget: €${template.minimumDailyBudget}`
      );
    }

    if (template.recommendedDailyBudget) {
      recommendations.push(
        `✨ Recommended budget: €${template.recommendedDailyBudget.min}-${template.recommendedDailyBudget.max}/day`
      );
    }

    return recommendations;
  },

  /**
   * Complete validation: Runtime + API v22
   */
  async validateComplete(
    template: IndustryTemplate,
    orgId: string
  ): Promise<{
    runtime: RuntimeRequirementsResult;
    apiCompliance: TemplateValidationResult;
    isReady: boolean;
  }> {
    const runtime = await this.checkTemplateRequirements(template, orgId);
    const apiCompliance = this.validateApiV22Compliance(template);

    return {
      runtime,
      apiCompliance,
      isReady: runtime.canProceed && apiCompliance.isValid
    };
  }
};

